/**
 * Wetter-Karte: EINE reine Abbildung `Datenlage -> Zustand -> Satz + hoechstens eine Handlung`,
 * dazu die als solche gekennzeichnete Ort-Typ-Vermutung — und (R4.3) die Catering-Folgerung aus
 * einer VORLIEGENDEN Prognose.
 *
 * Bauplan: docs/KONZEPT-RECHERCHE-SICHTBARKEIT.md § 4.5 (Mechanismus gegen Befund B4) und § 2.4
 * ("Bei `off_site` ist Regen kein Small Talk, sondern Logistik"), Kriterien R0.11 und R4.3 in
 * docs/LOOP-RECHERCHE.md. Gegenstueck zur Route (R0.10, PR #424), die den Grund liefert, statt ihn
 * wegzuwerfen.
 *
 * ── Warum ein eigenes Paket ───────────────────────────────────────────────────────────────────
 * `apps/web` hat KEINEN Test-Runner (kein `test`-Skript, kein vitest, keine `*.test.tsx`). Ein
 * Zustandsbaum in der React-Komponente waere damit unpruefbar — genau der Fall, in dem eine
 * Anzeige leise in "Keine Wetterdaten verfuegbar." zurueckfaellt, ohne dass ein Test es meldet.
 * Dieselbe Ueberlegung hat R0.7 zu `@maestro/anreicherung` gefuehrt (PR #422); dieses Paket
 * folgt ihr, statt sich dort einzunisten: `@maestro/anreicherung` bildet die NEUN Anreicherungs-
 * Zustaende ab und prueft ihre Vollstaendigkeit gegen `toHaveLength(9)` — ein Wetter-Zustand
 * darin waere eine stille Aenderung an einem abgenommenen Kriterium.
 *
 * Das Paket hat bewusst KEIN `test`-Skript: seine Tests liegen in
 * `apps/api/test/wetter-anzeige.test.ts`, weil genau diese Suite garantiert in der CI laeuft
 * (vier DB-Shards) — dieselbe Ueberlegung wie bei `anreicherung-zustand.test.ts`.
 *
 * ── Reinheit ─────────────────────────────────────────────────────────────────────────────────
 * Nur Standard-JavaScript, keine Uhr, kein Netz, kein DOM — damit `apps/web` es in den
 * Browser-Bundle nehmen kann und `apps/api` es im Test ohne Aufbau importiert.
 *
 * ── Was dieses Paket NICHT tut ───────────────────────────────────────────────────────────────
 * Es schreibt nichts. Die Vermutung ueber den Ort-Typ ist eine ANZEIGE: sie beschriftet einen
 * Weg zum Formular, sie fuellt es nicht vor und sie loest keinen Abruf aus. `events.location_type`
 * aendert sich dadurch nicht — "nie stilles Schreiben" (§ 4.5).
 */

/* ────────────────────────────── Eingabe ────────────────────────────────────────────────────── */

/** Der Ort-Typ, wie er in `events.location_type` steht (`null` = nicht gepflegt). */
export type OrtTyp = "in_house" | "off_site";

/**
 * Die fuenf Gruende, die `GET /api/events/:id/weather` neben `data: null` mitliefert (R0.10,
 * `apps/api/src/lib/weather.ts#WetterGrund`). Hier gespiegelt und nicht importiert, weil das
 * Paket bewusst nichts aus `apps/api` zieht; die Testdatei prueft beide Listen gegeneinander.
 */
export const WETTER_GRUENDE = [
  "kein_orttyp",
  "keine_adresse",
  "geocoding_fehlgeschlagen",
  "ausserhalb_fenster",
  "quelle_nicht_erreichbar",
] as const;
export type WetterGrund = (typeof WETTER_GRUENDE)[number];

export interface WetterLage {
  /** `events.location_type` — `null` heisst "nicht gepflegt", nicht "unbekannt". */
  ortTyp: OrtTyp | null;
  /** Ist am Vorgang ueberhaupt ein Termin gesetzt? */
  hatTermin: boolean;
  /** Client-Spiegel des Prognosefensters (~ -1 bis +10 Tage). Ohne Termin immer `false`. */
  imFenster: boolean;
  /** Laeuft der Abruf gerade? */
  laedt: boolean;
  /** Liegt eine Prognose vor (`data` der Route ist nicht null)? */
  prognoseDa: boolean;
  /**
   * Der Grund aus der Antwort (nur gesetzt, wenn `data` null war). `null` heisst: die Route wurde
   * nicht gefragt (Client-Vorfilter), oder sie hat einen Treffer geliefert, oder es antwortet
   * eine aeltere Fassung der Route ohne `reason`.
   */
  grund: WetterGrund | null;
  /** Fehlermeldung des Abrufs selbst (HTTP-Fehler, Netzfehler). `null` = kein Fehler. */
  fehler: string | null;
  /**
   * `inquiries.service_kind` der Anfrage, aus der dieser Vorgang entstanden ist — Signal fuer die
   * Vermutung, sonst nichts.
   */
  serviceKind?: "catering" | "event" | null;
  /**
   * Die Lieferadresse aus der Anfrage (`inquiries.details->>'deliveryAddress'`) — zweites Signal
   * fuer die Vermutung.
   */
  lieferadresse?: string | null;
  /**
   * WP5c (Migration 386, Auftrags-Dashboard „Phase C.1"): `events.setup_location` — Aufbau
   * Innen/Außen/Beides. Anders als `serviceKind`/`lieferadresse` KEIN Signal aus einer Anfrage,
   * sondern ein vom Menschen gepflegtes Feld — deshalb hat es Vorrang vor den beiden anderen
   * Signalen (s. `ortTypVermutung`).
   */
  setupLocation?: "indoor" | "outdoor" | "mixed" | null;
}

/* ────────────────────────────── Ausgabe ────────────────────────────────────────────────────── */

/**
 * Die unterscheidbaren Zustaende der Karte. `prognose` und `laedt` tragen keinen Satz — dort
 * sprechen die Daten bzw. der Ladebalken. Alle uebrigen tragen genau einen.
 */
export const WETTER_ZUSTAENDE = [
  "prognose",
  "laedt",
  "kein_termin",
  "kein_orttyp",
  "ausserhalb_fenster",
  "keine_adresse",
  "nicht_verortbar",
  "quelle_nicht_erreichbar",
  "abruf_fehlgeschlagen",
  "unbekannt",
] as const;
export type WetterZustand = (typeof WETTER_ZUSTAENDE)[number];

/** Die Zustaende mit Satz — Grundlage der Testabdeckung. */
export const WETTER_ZUSTAENDE_MIT_SATZ: readonly WetterZustand[] = WETTER_ZUSTAENDE.filter(
  (z) => z !== "prognose" && z !== "laedt",
);

/**
 * Welche Handlung die Oberflaeche anbieten darf. Jede Art hat ein BESTEHENDES Ziel — es gibt hier
 * bewusst keine Schaltflaeche, die nichts tut (Lehre aus R0.7: dort blieben sechs von neun
 * Zustaenden ohne Knopf, weil es fuer sie keinen Weg gab).
 *
 *  - `event_bearbeiten`     -> der Drawer "Eckdaten" auf derselben Seite (`AuftragEckdatenKarte`);
 *                              er enthaelt Termin, Ort-Typ UND den Ort-Freitext (`events.location`,
 *                              seit V-1164 Teil der Adress-Kette fuer `off_site`). V-1164
 *                              (28.09.2026): zeigte bis dahin auf den reduzierten Drawer "Vorgang
 *                              bearbeiten" — der hat seit WP3a (08.09.2026) weder Ort-Typ noch Ort
 *                              mehr, jeder Klick lief damit ins Leere.
 *  - `betriebsadresse`      -> `/einstellungen?tab=betrieb`, Karte "Firmen-Kopf" (Feld "Adresse").
 *                              Dieselbe Deep-Link-Form wie `MODUL_KACHEL_URL` in
 *                              `apps/web/src/pages/customer-detail.tsx` (PR #411, `?tab=`).
 *                              BEWUSST NICHT `?tab=module`: PR #411 hat die MODUL-SCHALTER in
 *                              ihre Kacheln geholt; die Betriebsadresse ist Stammdaten und steht
 *                              weiterhin im Tab "Betrieb". Ein Sprung in die Wetter-Kachel waere
 *                              formal "die Modul-Kachel", aber dort laesst sich keine Adresse
 *                              pflegen — also eine Sackgasse.
 *  - `erneut_versuchen`     -> derselbe Abruf noch einmal (reload des Hooks), NUR wo eine
 *                              Wiederholung ueberhaupt etwas aendern kann.
 *
 * V-1164: `kundenprofil` (bis dahin das Ziel fuer `off_site` + `keine_adresse`/
 * `geocoding_fehlgeschlagen`) ist ENTFALLEN. Seit die Adress-Kette der Route (`routes/weather.ts`)
 * fuer `off_site` VOR der Kundenadresse Lieferadresse, Auftragsadresse UND den Ort-Freitext prueft,
 * heisst `keine_adresse` fuer `off_site`: nicht "der Kunde hat keine Adresse", sondern "am Vorgang
 * UND beim Kunden steht nirgends ein Ort" — und der Ort-Freitext im Eckdaten-Drawer ist der
 * schnellste Weg, das zu beheben (er sticht die Kundenadresse ohnehin, sobald er gefuellt ist).
 */
export type WetterHandlungsArt = "event_bearbeiten" | "betriebsadresse" | "erneut_versuchen";

export interface WetterHandlung {
  art: WetterHandlungsArt;
  beschriftung: string;
}

/**
 * Eine ANNAHME ueber den Ort-Typ — nie ein Wert, der aussieht wie eine gepflegte Angabe.
 * `satz` traegt das Wort "Vermutung" und den Zusatz "nicht gespeichert" im Klartext, damit die
 * Kennzeichnung nicht an einer Formatierung haengt, die eine spaetere Umgestaltung verliert.
 */
export interface OrtTypVermutung {
  ortTyp: OrtTyp;
  quelle: "service_kind" | "lieferadresse" | "setup_location";
  satz: string;
}

export interface WetterAnzeige {
  zustand: WetterZustand;
  /** Genau ein Satz — `null` nur bei `prognose` und `laedt`. */
  satz: string | null;
  /** Genau eine Handlung — `null`, wo es keinen Weg gibt. */
  handlung: WetterHandlung | null;
  /** Nur bei `kein_orttyp` und nur, wenn die Signale eindeutig sind. */
  vermutung: OrtTypVermutung | null;
}

/* ────────────────────────────── Die Vermutung ──────────────────────────────────────────────── */

const VERMUTUNG_PRAEFIX = "Vermutung (nicht gespeichert): ";

/**
 * Leitet aus `service_kind` bzw. der Lieferadresse einen wahrscheinlichen Ort-Typ ab.
 *
 * Reihenfolge und Grenzen:
 *  0. WP5c (Migration 386): `setup_location = 'outdoor'` ist KEIN Text-Rateweg mehr, sondern ein
 *     vom Menschen explizit gepflegtes Feld — und schlaegt deshalb auch `service_kind`/
 *     Lieferadresse. Ein Außen-Aufbau am eigenen Betriebssitz ist der seltene Ausnahmefall (Garten/
 *     Terrasse), ein Außen-Aufbau beim Kunden/an einem fremden Ort die weit haeufigere Lesart —
 *     bleibt trotzdem eine ANNAHME, keine Festlegung, deshalb weiterhin `VERMUTUNG_PRAEFIX`.
 *     `'indoor'`/`'mixed'` entscheiden nichts eindeutig (eine Halle kann trotzdem "außer Haus"
 *     sein) und fallen auf die bisherigen Signale zurueck.
 *  1. `service_kind` ist eine AUSDRUECKLICHE Angabe des Kunden ("Im Haus" / "Vor Ort" im
 *     Anfrage-Formular, `apps/api/src/lib/widget-formular.ts`) und schlaegt jedes Indiz.
 *  2. Eine Lieferadresse ist nur ein Indiz — sie zaehlt, wenn `service_kind` fehlt.
 *  3. WIDERSPRUCH (`service_kind = 'event'`, aber eine Lieferadresse liegt vor): KEINE Vermutung.
 *     Zwei Signale, die sich widersprechen, ergeben keine Wahrscheinlichkeit, sondern eine offene
 *     Frage. Ein selbstbewusst falscher Vorschlag waere schlimmer als gar keiner.
 */
export function ortTypVermutung(
  lage: Pick<WetterLage, "serviceKind" | "lieferadresse" | "setupLocation">,
): OrtTypVermutung | null {
  const lieferadresse = (lage.lieferadresse ?? "").trim();
  const hatLieferadresse = lieferadresse.length > 0;

  if (lage.setupLocation === "outdoor") {
    return {
      ortTyp: "off_site",
      quelle: "setup_location",
      satz: `${VERMUTUNG_PRAEFIX}außer Haus — der Aufbau ist als „Außen" hinterlegt.`,
    };
  }

  if (lage.serviceKind === "catering") {
    return {
      ortTyp: "off_site",
      quelle: "service_kind",
      satz: `${VERMUTUNG_PRAEFIX}außer Haus — die Anfrage war als Catering gekennzeichnet.`,
    };
  }
  if (lage.serviceKind === "event") {
    if (hatLieferadresse) return null; // Widerspruch, siehe oben.
    return {
      ortTyp: "in_house",
      quelle: "service_kind",
      satz: `${VERMUTUNG_PRAEFIX}im Haus — die Anfrage war als Veranstaltung im Haus gekennzeichnet.`,
    };
  }
  if (hatLieferadresse) {
    return {
      ortTyp: "off_site",
      quelle: "lieferadresse",
      satz: `${VERMUTUNG_PRAEFIX}außer Haus — zur Anfrage liegt eine Lieferadresse vor.`,
    };
  }
  return null;
}

/* ────────────────────────────── Die Abbildung ──────────────────────────────────────────────── */

/** Wo die Adresse steht, die gerade fehlt bzw. sich nicht verorten liess. V-1164: bei `off_site`
 *  ist das seit der erweiterten Adress-Kette (Lieferadresse -> Auftragsadresse -> Ort-Freitext ->
 *  Kundenadresse) primaer der Ort-Freitext im Eckdaten-Drawer, nicht mehr das Kundenprofil. */
function adressHandlung(ortTyp: OrtTyp | null): WetterHandlung {
  return ortTyp === "off_site"
    ? { art: "event_bearbeiten", beschriftung: "Ort ergänzen" }
    : { art: "betriebsadresse", beschriftung: "Betriebsadresse ergänzen" };
}

/**
 * Bildet die Datenlage auf genau einen Zustand, genau einen Satz und hoechstens eine Handlung ab.
 *
 * Reihenfolge der Pruefungen (jede Zeile hat einen Grund):
 *  1./2./3. Was der Betreiber selbst pflegen muss, kommt vor allem, was ein Abruf ergeben haette
 *     — ohne Termin und ohne Ort-Typ wird die Route gar nicht erst gefragt (Vorfilter in
 *     `event-detail.tsx`), es gaebe also gar keinen `grund` zu zeigen.
 *  4. Laden vor Ergebnis: waehrend eines Nachladens steht der vorige `grund` noch im State.
 *  5. Ein Fehler des Abrufs selbst schlaegt jeden Inhalt — es gibt keinen.
 *  6. Treffer.
 *  7. Erst jetzt der Grund aus der Antwort.
 *  8. Kein Grund und kein Treffer: eine aeltere Fassung der Route. Der alte Einheitssatz bleibt
 *     genau fuer diesen einen Fall stehen — er ist dort die WAHRE Aussage.
 */
export function wetterAnzeige(lage: WetterLage): WetterAnzeige {
  const ohne = { handlung: null, vermutung: null } as const;

  // 1. Kein Termin.
  if (!lage.hatTermin) {
    return {
      zustand: "kein_termin",
      satz: "Kein Termin gesetzt — ohne Datum gibt es keine Prognose.",
      handlung: { art: "event_bearbeiten", beschriftung: "Termin ergänzen" },
      vermutung: null,
    };
  }

  // 2. Ort-Typ nicht gepflegt — der einzige Zustand mit Vermutung.
  if (!lage.ortTyp) {
    return {
      zustand: "kein_orttyp",
      satz: "Ort-Typ nicht gepflegt — er entscheidet, welche Adresse gilt.",
      handlung: { art: "event_bearbeiten", beschriftung: "Ort-Typ ergänzen" },
      vermutung: ortTypVermutung(lage),
    };
  }

  // 3. Ausserhalb des Prognosefensters — hier gibt es nichts zu pflegen, nur zu warten.
  if (!lage.imFenster) {
    return {
      zustand: "ausserhalb_fenster",
      satz: "Prognose erst ab ca. 10 Tage vor dem Termin verfügbar.",
      ...ohne,
    };
  }

  // 4. Abruf laeuft.
  if (lage.laedt) return { zustand: "laedt", satz: null, ...ohne };

  // 5. Der Abruf selbst ist gescheitert.
  if (lage.fehler) {
    return {
      zustand: "abruf_fehlgeschlagen",
      satz: "Die Wetterdaten konnten nicht geladen werden.",
      handlung: { art: "erneut_versuchen", beschriftung: "Erneut versuchen" },
      vermutung: null,
    };
  }

  // 6. Treffer.
  if (lage.prognoseDa) return { zustand: "prognose", satz: null, ...ohne };

  // 7. Der Grund aus der Antwort (R0.10).
  switch (lage.grund) {
    case "keine_adresse":
      return {
        zustand: "keine_adresse",
        // V-1164: bei `off_site` prueft die Route inzwischen Lieferadresse, Auftragsadresse UND
        // den Ort-Freitext, BEVOR sie auf die Kundenadresse zurueckfaellt — "keine_adresse" heisst
        // hier also "nirgends am Vorgang UND beim Kunden ein Ort", nicht mehr "der Kunde hat keine
        // Adresse gepflegt" (das waere seit der erweiterten Kette eine falsche Zuschreibung).
        satz:
          lage.ortTyp === "off_site"
            ? "Für diesen Vorgang ist kein Ort hinterlegt — ohne ihn lässt sich keine Prognose abrufen."
            : "Keine Betriebsadresse hinterlegt — ohne sie lässt sich der Ort nicht bestimmen.",
        handlung: adressHandlung(lage.ortTyp),
        vermutung: null,
      };
    case "geocoding_fehlgeschlagen":
      return {
        zustand: "nicht_verortbar",
        // V-1164: der hinterlegte Ort kann bei `off_site` jetzt aus vier Quellen stammen
        // (Lieferadresse/Auftragsadresse/Ort-Freitext/Kundenadresse) — der Satz benennt darum
        // keine mehr namentlich, sondern nur noch, DASS die Zuordnung fehlschlug (die exakte
        // Ursache: ein Ort ist hinterlegt, der Geocoder findet dafür keine Koordinate).
        satz:
          lage.ortTyp === "off_site"
            ? "Adresse nicht verortbar — der hinterlegte Ort ließ sich keiner Koordinate zuordnen."
            : "Adresse nicht verortbar — die Betriebsadresse ließ sich keiner Koordinate zuordnen.",
        handlung: adressHandlung(lage.ortTyp),
        vermutung: null,
      };
    case "quelle_nicht_erreichbar":
      // Der EINZIGE Zustand, bei dem der Betreiber nichts pflegen kann — und damit der einzige,
      // in dem eine Wiederholung sinnvoll ist.
      return {
        zustand: "quelle_nicht_erreichbar",
        satz: "Quelle nicht erreichbar — Bright Sky (DWD) hat gerade nicht geantwortet.",
        handlung: { art: "erneut_versuchen", beschriftung: "Erneut versuchen" },
        vermutung: null,
      };
    case "ausserhalb_fenster":
      // Der Server rechnet an der Mitternachtsgrenze anders als der Client-Spiegel — er gewinnt.
      return { zustand: "ausserhalb_fenster", satz: "Prognose erst ab ca. 10 Tage vor dem Termin verfügbar.", ...ohne };
    case "kein_orttyp":
      // Der Server kennt einen Ort-Typ nicht, den der Client zu kennen glaubte (unbekannter Wert
      // in der Spalte). Auch hier gewinnt der Server.
      return {
        zustand: "kein_orttyp",
        satz: "Ort-Typ nicht gepflegt — er entscheidet, welche Adresse gilt.",
        handlung: { art: "event_bearbeiten", beschriftung: "Ort-Typ ergänzen" },
        vermutung: ortTypVermutung(lage),
      };
    default:
      // 8. Weder Treffer noch Grund.
      return { zustand: "unbekannt", satz: "Keine Wetterdaten verfügbar.", ...ohne };
  }
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * R4.3 — die Catering-Folgerung (Konzept § 2.4)
 *
 * "Bei `off_site` ist Regen kein Small Talk, sondern Logistik. Die Karte bekommt eine
 * Klartext-Folgerung (12 mm Regen — Aufbau im Freien pruefen), abgeleitet aus
 * `precipitationMm`/`tempMinC` durch eine DETERMINISTISCHE REGEL IM CODE, nicht durch das Modell."
 * (§ 2.4, woertlich; die Zahl 12 ist dort ein Beispiel, kein Schwellenwert.)
 *
 * ── S2.1, Antoine 07.08.2026: auch bei Terminen IM HAUS ──────────────────────────────────────
 * Woertlich: „Wetter auch bei Terminen im Haus wegen Terrasse etc." Die urspruengliche Fassung
 * schwieg bei `in_house` vollstaendig — begruendet mit „bei in_house entscheidet das Wetter
 * nichts ueber den Aufbau". Diese Begruendung war zu eng gedacht: ein Haus mit Terrasse hat eine
 * Aussenflaeche, auch wenn der Termin „im Haus" gepflegt ist. Der Ort-Typ sagt, WELCHE Adresse
 * gilt — er sagt nicht, dass es dort keinen Aussenbereich gibt.
 *
 * Die Schwellen sind DIESELBEN (es ist dasselbe Wetter). Verschieden ist nur, was der Satz
 * benennt:
 *  - `off_site`: der Aufbau im Freien IST die Veranstaltung -> der Satz endet in einer Pruefung
 *    ("Aufbau im Freien pruefen") — unveraendert, wie seit PR #449 abgenommen.
 *  - `in_house`: die Aussenflaeche ist ein TEIL des Hauses -> der Satz bleibt rein BESCHREIBEND.
 *    Er nennt den Messwert und die Flaeche, die er betrifft. Er sagt NICHT, ob draussen gedeckt
 *    werden soll, ob die Terrasse zu nutzen ist oder ob das Wetter gut oder schlecht ist. Das
 *    entscheidet der Betreiber, der sein Haus kennt — genau die Grenze, die schon
 *    `betriebsstatusWarnung()` in `@maestro/places-anzeige` (R4.2) haelt.
 *
 * ── Warum eine Schwellenwert-Tabelle und kein Sprachmodell ────────────────────────────────────
 * Ein Modell, das aus 12 mm einen Rat formuliert, ist nicht pruefbar (heute anders als morgen),
 * nicht wiederholbar und nicht testbar. Eine Tabelle ist alles drei — deshalb ist der geforderte
 * Beweis ein Tabellentest (`apps/api/test/wetter-folgerung.test.ts`).
 *
 * ── Was diese Regel NICHT tut ────────────────────────────────────────────────────────────────
 *  - Sie BEWERTET das Wetter nicht ("schlechtes Wetter", Ampel, Punktzahl) und raet nicht ab.
 *    Sie nennt die Zahl und den Ort, an dem sie Folgen hat. Die Entscheidung bleibt beim Menschen
 *    — dasselbe Prinzip wie `betriebsstatusWarnung()` in `@maestro/places-anzeige` (R4.2).
 *  - Sie erzeugt KEINEN Betrag und keine Preisfolge. Aus Wetter folgt nie eine Zahl im Angebot
 *    (Invariante: Preise kommen aus Katalog + `packages/pricing`).
 *  - Sie folgert NICHT auf einer Vermutung, siehe `wetterFolgerung()`.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Regen-UNTERGRENZE: 2,5 mm Tagessumme. HERGELEITET — § 2.4 nennt keinen Wert (die dortigen 12 mm
 * sind der Beispielsatz), und der DWD kennt fuer "Regen, der einen Aufbau angeht" keine Definition.
 *
 * Die Herleitung, damit sie nachpruefbar bleibt statt geraten zu wirken:
 *  1. NICHT genommen: die naheliegende amtliche Zahl. Der DWD definiert einen "Regentag" als Tag
 *     mit einer 24-stuendigen Regenhoehe >= 0,1 mm (Wetterlexikon, Stichwort "Niederschlagstag"/
 *     "Regentag", https://www.dwd.de/DE/service/lexikon/Functions/glossar.html?lv3=101910&lv2=101812).
 *     Bei 0,1 mm stuende der Hinweis an jedem zweiten Tag im Jahr — und ein Hinweis, der immer
 *     dasteht, wird nach zwei Wochen ueberlesen (dieselbe Ueberlegung wie bei
 *     `CLOSED_TEMPORARILY` in R4.2).
 *  2. GENOMMEN: die Intensitaets-Klassifikation des DWD. "Leichter Regen" ist dort eine
 *     Niederschlagshoehe von < 2,5 mm in 60 Minuten; ab 2,5 mm/h beginnt "maessiger Regen"
 *     (Wetterlexikon, Stichwort "Niederschlagsintensitaet",
 *     https://www.dwd.de/DE/service/lexikon/Functions/glossar.html?lv3=101906&lv2=101812).
 *  3. UEBERTRAGEN: Eine Tagessumme UNTER 2,5 mm kann rechnerisch nicht einmal eine einzige Stunde
 *     maessigen Regens enthalten — der ganze Tag bleibt im Band "leicht". Der Schwellenwert ist
 *     damit bewusst KONSERVATIV: er zeigt den Hinweis eher zu frueh als zu spaet, denn ein
 *     uebersehener Aufbau kostet mehr als ein Satz zu viel.
 */
export const REGEN_HINWEIS_AB_MM = 2.5;

/**
 * Regen-OBERGRENZE: 30 mm Tagessumme — ab hier ein deutlicherer Satz.
 *
 * ZITIERT, nicht hergeleitet: Es ist die untere Grenze des DWD-Warnkriteriums "Dauerregen",
 * Warnstufe 2 (markantes Wetter): "30 bis 50 l/m2 in 24 Stunden"
 * (https://www.dwd.de/DE/wetter/warnungen_aktuell/kriterien/warnkriterien.html, abgerufen
 * 06.08.2026). Von den vier dort genannten Zeitfenstern (12/24/48/72 h) ist 24 h das einzige, das
 * zur Tagessumme aus `lib/weather.ts` passt (l/m2 = mm) — die 12-Stunden-Zahl (25 l/m2) waere hier
 * die falsche Zeile.
 *
 * WAS DER SATZ DESHALB NICHT BEHAUPTET: dass eine Warnung vorliegt. Der DWD warnt fuer seine
 * eigenen Vorhersage-Zeitfenster, unsere Zahl ist eine Kalendertagssumme — ein Tag mit 30 mm muss
 * das 24-Stunden-Kriterium nicht getroffen haben. Der Satz sagt darum "Groessenordnung".
 */
export const REGEN_VIEL_AB_MM = 30;

/**
 * Frost: Tagestiefstwert unter 0 °C. ZITIERT — DWD-Wetterlexikon, "Frosttag": ein Tag, an dem das
 * Minimum der Lufttemperatur unterhalb des Gefrierpunktes (0 °C) liegt
 * (https://www.dwd.de/DE/service/lexikon/Functions/glossar.html?lv2=100784&lv3=100912).
 * Kein eigener Wert noetig: der Gefrierpunkt ist die Grenze, an der sich fuer einen Aufbau im
 * Freien real etwas aendert (Wasser, Getraenke, Standzeit der Gaeste).
 */
export const FROST_UNTER_C = 0;

/**
 * Hitze: Tageshoechstwert ab 30 °C. ZITIERT — DWD-Wetterlexikon, "Heisser Tag": ein Tag, an dem
 * das Maximum der Lufttemperatur >= 30 °C betraegt
 * (https://www.dwd.de/DE/service/lexikon/Functions/glossar.html?lv3=101162&lv2=101094).
 * Fuer ein Catering ist das die Kuehlketten-Frage; der Satz stellt sie, er beantwortet sie nicht.
 */
export const HEISSER_TAG_AB_C = 30;

/**
 * Was die Folgerung braucht: den GEPFLEGTEN Ort-Typ und die Prognose-Werte.
 *
 * `ortTyp` ist hier bewusst derselbe Typ wie in `WetterLage` (`null` moeglich) und NICHT auf
 * `"off_site"` verengt: die Regel soll den Gegenfall selbst entscheiden und ihn testbar machen,
 * statt ihn dem Aufrufer zu ueberlassen.
 */
export interface WetterFolgerungLage {
  /** `events.location_type`, wie gepflegt. Eine VERMUTUNG gehoert hier nicht hinein (siehe unten). */
  ortTyp: OrtTyp | null;
  /** Tagessumme Niederschlag in mm (`DayForecast.precipitationMm`). `null` = kein Wert geliefert. */
  precipitationMm: number | null;
  /** Tagestiefstwert in °C (`DayForecast.tempMinC`). */
  tempMinC: number | null;
  /** Tageshoechstwert in °C (`DayForecast.tempMaxC`). */
  tempMaxC: number | null;
}

export interface WetterFolgerung {
  /**
   * Ein bis zwei Saetze: hoechstens einer zum Niederschlag und hoechstens einer zur Temperatur.
   * Nie leer — gaebe es nichts zu sagen, ist die ganze Folgerung `null`.
   */
  readonly saetze: readonly string[];
}

/** "12" / "12,4" — deutsche Schreibweise, hoechstens eine Nachkommastelle (Bright Sky rundet dort). */
function mmText(wert: number): string {
  return (Math.round(wert * 10) / 10).toString().replace(".", ",");
}

/**
 * "-2" / "31" — ganze Grad, wie auf der Karte (`Math.round`, event-detail.tsx).
 *
 * Die `+ 0`-Zeile faengt `-0` ab: `Math.round(-0.4)` ist in JavaScript `-0`, und `String(-0)` ist
 * "0" — aber `-0` in eine Zeichenkette einzusetzen bleibt eine Falle, sobald jemand hier
 * `toFixed`/`Intl` einsetzt. Gemeint ist die Anzeige, nicht die Einordnung: ob Frost vorliegt,
 * entscheidet der ROHE Wert (-0,4 °C ist Frost), gerundet wird erst der Text.
 */
function gradText(wert: number): string {
  return String(Math.round(wert) + 0);
}

/**
 * Der Nachsatz der IM-HAUS-Fassung (S2.1). Er benennt die Flaeche, um die es geht, und hoert dort
 * auf: keine Empfehlung ("draussen decken"), keine Bewertung ("ungemuetlich"), keine Frage. Wer
 * sein Haus kennt, weiss selbst, was 12 mm Regen fuer seine Terrasse heissen — die Software weiss
 * es nicht und behauptet es deshalb nicht.
 */
const AUSSENFLAECHE = "das betrifft die Außenflächen des Hauses, etwa die Terrasse.";

/**
 * Die Wetter-Folgerung — oder `null`.
 *
 * `null` heisst: hier gibt es nichts zu folgern. Das gilt in drei Faellen, und jeder hat einen
 * Grund:
 *
 *  1. **`ortTyp === null` (nicht gepflegt)** — und zwar AUCH DANN, wenn `ortTypVermutung()` fuer
 *     denselben Vorgang "ausser Haus" vermutet. Eine Vermutung traegt sichtbar
 *     "Vermutung (nicht gespeichert)" (R0.11); eine Folgerung ist eine harte Aussage. Beides zu
 *     verbinden hiesse, aus einem Indiz eine Handlungsanweisung zu machen — genau der Rueckschritt,
 *     den R0.11 verhindert hat. Praktisch kann der Fall ohnehin nicht eintreten: ohne gepflegten
 *     Ort-Typ antwortet die Route mit `reason: 'kein_orttyp'` und OHNE Prognose
 *     (`routes/weather.ts`), es gibt also gar keine Werte. Die Regel haelt es trotzdem fest, damit
 *     ein spaeterer Umbau der Route diese Grenze nicht unbemerkt verschiebt.
 *  2. **Keine Werte** (`precipitationMm`/`tempMinC`/`tempMaxC` alle `null`) — dann gibt es keine
 *     Datenlage, nur ein Datum.
 *  3. **Werte unterhalb aller Schwellen** — trockener, milder Tag. Kein Satz ist die richtige
 *     Aussage; "kein besonderes Wetter" waere eine Zeile ohne Inhalt.
 *
 * NICHT mehr `null` ist seit S2.1 der Fall `in_house` (Antoine, 07.08.2026): dort steht dieselbe
 * Datenlage in der BESCHREIBENDEN Fassung, siehe Kopf dieses Abschnitts.
 *
 * Reihenfolge der Saetze: Niederschlag vor Temperatur (§ 2.4 nennt den Regen als den Fall, der aus
 * Small Talk Logistik macht). Frost und Hitze schliessen einander aus — bei widerspruechlichen
 * Werten (Tiefstwert unter 0, Hoechstwert ueber 30) gewinnt der Frost, weil er den Aufbau selbst
 * betrifft und nicht nur die Ware.
 */
export function wetterFolgerung(lage: WetterFolgerungLage): WetterFolgerung | null {
  if (lage.ortTyp == null) return null;
  /** Ist die Veranstaltung SELBST draussen? Dann geht es um den Aufbau, nicht um eine Teilflaeche. */
  const draussen = lage.ortTyp === "off_site";

  const saetze: string[] = [];

  const regen = lage.precipitationMm;
  if (regen != null && regen >= REGEN_VIEL_AB_MM) {
    saetze.push(
      `${mmText(regen)} mm Regen — das ist die Größenordnung einer DWD-Dauerregenwarnung; ` +
        (draussen ? `Aufbau im Freien, Zelt und Zufahrt prüfen.` : AUSSENFLAECHE),
    );
  } else if (regen != null && regen >= REGEN_HINWEIS_AB_MM) {
    // Der Satz aus § 2.4, woertlich — und daneben die im-Haus-Fassung derselben Tatsache.
    saetze.push(draussen ? `${mmText(regen)} mm Regen — Aufbau im Freien prüfen.` : `${mmText(regen)} mm Regen — ${AUSSENFLAECHE}`);
  }

  if (lage.tempMinC != null && lage.tempMinC < FROST_UNTER_C) {
    saetze.push(
      `Tiefstwert ${gradText(lage.tempMinC)} °C — Frost; ` +
        (draussen ? `Aufbau im Freien und Wärme für die Gäste prüfen.` : AUSSENFLAECHE),
    );
  } else if (lage.tempMaxC != null && lage.tempMaxC >= HEISSER_TAG_AB_C) {
    saetze.push(
      `Höchstwert ${gradText(lage.tempMaxC)} °C — heißer Tag; ` + (draussen ? `Kühlkette und Schatten prüfen.` : AUSSENFLAECHE),
    );
  }

  return saetze.length > 0 ? Object.freeze({ saetze: Object.freeze(saetze) }) : null;
}
